import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { MOBILE_MEDIA_QUERY } from '../../../config/constants';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { WIDGETS_GAP_DESKTOP, WIDGETS_GAP_MOBILE, WIDGET_SMALL_WIDTH, WIDGET_WIDE_WIDTH } from '../constants';
import type { WidgetSizeType } from '../types';

interface UseWidgetLayoutProps {
  widgetsCount: number;
}

function areTypesEqual(a: WidgetSizeType[], b: WidgetSizeType[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function getWidgetWidth(type: WidgetSizeType, isMobile: boolean): number {
  if (type === 'wide') {
    return isMobile ? WIDGET_WIDE_WIDTH.mobile : WIDGET_WIDE_WIDTH.desktop;
  }
  return isMobile ? WIDGET_SMALL_WIDTH.mobile : WIDGET_SMALL_WIDTH.desktop;
}

function computeWidgetTypes(containerWidth: number, widgetsCount: number, isMobile: boolean): WidgetSizeType[] {
  if (!widgetsCount) {
    return [];
  }

  const gap = isMobile ? WIDGETS_GAP_MOBILE : WIDGETS_GAP_DESKTOP;
  const wideWidth = getWidgetWidth('wide', isMobile);
  const allWideTotal = widgetsCount * wideWidth + Math.max(0, widgetsCount - 1) * gap;

  if (allWideTotal <= containerWidth) {
    return Array<WidgetSizeType>(widgetsCount).fill('wide');
  }
  return Array<WidgetSizeType>(widgetsCount).fill('small');
}

export function useWidgetLayout(props: UseWidgetLayoutProps) {
  const { widgetsCount } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  const [widgetSizeTypes, setWidgetSizeTypes] = useState<WidgetSizeType[]>(() =>
    Array<WidgetSizeType>(widgetsCount).fill('wide'),
  );
  const [allWidgetsVisible, setAllWidgetsVisible] = useState(true);
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

  const updateLayout = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const types = computeWidgetTypes(container.clientWidth, widgetsCount, isMobile);
    setWidgetSizeTypes((prev) => (areTypesEqual(prev, types) ? prev : types));

    const gap = isMobile ? WIDGETS_GAP_MOBILE : WIDGETS_GAP_DESKTOP;
    const smallWidth = getWidgetWidth('small', isMobile);
    const allSmallWidth = widgetsCount * smallWidth + Math.max(0, widgetsCount - 1) * gap;
    setAllWidgetsVisible(allSmallWidth <= container.clientWidth);
  }, [isMobile, widgetsCount]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(updateLayout);
    observer.observe(container);
    updateLayout();

    return () => observer.disconnect();
  }, [updateLayout]);

  const effectiveWidgetSizeTypes =
    widgetSizeTypes.length === widgetsCount ? widgetSizeTypes : Array<WidgetSizeType>(widgetsCount).fill('wide');

  return { containerRef, widgetSizeTypes: effectiveWidgetSizeTypes, allWidgetsVisible };
}
